import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Track, TrackCategory, TrackMood, TrackTempo, TrackKey, TrackInstrument, DownloadRequest } from '../models/track.model';

@Injectable({
  providedIn: 'root'
})
export class MusicService {
  private apiUrl = 'http://localhost:8080/api'; // Fallback if environment.apiUrl is not defined
  
  // BehaviorSubjects for reactive data
  private tracksSubject = new BehaviorSubject<Track[]>([]);
  private categoriesSubject = new BehaviorSubject<TrackCategory[]>([]);
  private moodsSubject = new BehaviorSubject<TrackMood[]>([]);
  private temposSubject = new BehaviorSubject<TrackTempo[]>([]);
  private keysSubject = new BehaviorSubject<TrackKey[]>([]);
  private instrumentsSubject = new BehaviorSubject<TrackInstrument[]>([]);
  
  // Observable streams
  public tracks$ = this.tracksSubject.asObservable();
  public categories$ = this.categoriesSubject.asObservable();
  public moods$ = this.moodsSubject.asObservable();
  public tempos$ = this.temposSubject.asObservable();
  public keys$ = this.keysSubject.asObservable();
  public instruments$ = this.instrumentsSubject.asObservable();
  
  // Mock data for development (will be replaced with API calls)
  private mockCategories: TrackCategory[] = [
    { id: 'all', name: 'All' },
    { id: 'soundtracks', name: 'Soundtracks', description: 'Complete musical compositions for various media', imageUrl: 'assets/images/categories/soundtracks.jpg' },
    { id: 'jingles', name: 'Jingles', description: 'Short musical pieces for commercials and branding', imageUrl: 'assets/images/categories/jingles.jpg' },
    { id: 'voiceovers', name: 'Voice Overs', description: 'Professional voice recordings for various projects', imageUrl: 'assets/images/categories/voiceovers.jpg' },
    { id: 'hymns', name: 'Hymns', description: 'Inspirational and spiritual musical compositions', imageUrl: 'assets/images/categories/hymns.jpg' },
    { id: 'sound_design', name: 'Sound Design', description: 'Sound effects and atmospheric audio elements', imageUrl: 'assets/images/categories/sound_design.jpg' },
    { id: 'cinematic', name: 'Cinematic', description: 'Epic orchestral compositions for film and video', imageUrl: 'assets/images/categories/cinematic.jpg' },
    { id: 'corporate', name: 'Corporate', description: 'Professional music for business presentations', imageUrl: 'assets/images/categories/corporate.jpg' }
  ];
  
  private mockMoods: TrackMood[] = [
    { id: 'all', name: 'All Moods' },
    { id: 'energetic', name: 'Energetic', color: '#FF5722' },
    { id: 'calm', name: 'Calm', color: '#4CAF50' },
    { id: 'happy', name: 'Happy', color: '#FFC107' },
    { id: 'sad', name: 'Sad', color: '#2196F3' },
    { id: 'epic', name: 'Epic', color: '#9C27B0' },
    { id: 'mysterious', name: 'Mysterious', color: '#673AB7' },
    { id: 'romantic', name: 'Romantic', color: '#E91E63' },
    { id: 'corporate', name: 'Corporate', color: '#607D8B' },
    { id: 'inspirational', name: 'Inspirational', color: '#00BCD4' },
    { id: 'dramatic', name: 'Dramatic', color: '#795548' },
    { id: 'uplifting', name: 'Uplifting', color: '#8BC34A' }
  ];
  
  private mockTempos: TrackTempo[] = [
    { id: 'all', name: 'All Tempos' },
    { id: 'very_slow', name: 'Very Slow (40-60 BPM)' },
    { id: 'slow', name: 'Slow (60-80 BPM)' },
    { id: 'medium', name: 'Medium (80-110 BPM)' },
    { id: 'fast', name: 'Fast (110-140 BPM)' },
    { id: 'very_fast', name: 'Very Fast (140+ BPM)' }
  ];
  
  private mockKeys: TrackKey[] = [
    { id: 'all', name: 'All Keys' },
    { id: 'c_major', name: 'C Major' },
    { id: 'a_minor', name: 'A Minor' },
    { id: 'g_major', name: 'G Major' },
    { id: 'e_minor', name: 'E Minor' },
    { id: 'd_major', name: 'D Major' },
    { id: 'b_minor', name: 'B Minor' },
    { id: 'a_major', name: 'A Major' },
    { id: 'f_sharp_minor', name: 'F# Minor' },
    { id: 'e_major', name: 'E Major' },
    { id: 'c_sharp_minor', name: 'C# Minor' },
    { id: 'b_major', name: 'B Major' },
    { id: 'g_sharp_minor', name: 'G# Minor' }
  ];
  
  private mockInstruments: TrackInstrument[] = [
    { id: 'all', name: 'All Instruments' },
    { id: 'piano', name: 'Piano', category: 'keys' },
    { id: 'guitar', name: 'Guitar', category: 'strings' },
    { id: 'drums', name: 'Drums', category: 'percussion' },
    { id: 'bass', name: 'Bass', category: 'strings' },
    { id: 'violin', name: 'Violin', category: 'strings' },
    { id: 'cello', name: 'Cello', category: 'strings' },
    { id: 'flute', name: 'Flute', category: 'woodwinds' },
    { id: 'saxophone', name: 'Saxophone', category: 'woodwinds' },
    { id: 'trumpet', name: 'Trumpet', category: 'brass' },
    { id: 'synthesizer', name: 'Synthesizer', category: 'electronic' },
    { id: 'vocals', name: 'Vocals', category: 'voice' }
  ];
  
  // Enhanced track data with more metadata
  private mockTracks: Track[] = [
    {
      id: 1,
      title: "Bad Boy (Radio Edit)",
      artist: "Jeyslee",
      duration: "1:58",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "soundtracks",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "c_minor",
      bpm: 128,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["electronic", "dance", "radio"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/badboy%5D.png",
      releaseDate: "2023-05-15",
      description: "Energetic electronic track perfect for commercials and promotional videos."
    },
    {
      id: 2,
      title: "La Voie (Instrumental)",
      artist: "Jeyslee",
      duration: "3:52",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "soundtracks",
      mood: ["calm", "mysterious"],
      tempo: "medium",
      key: "g_minor",
      bpm: 95,
      instruments: ["piano", "strings", "synthesizer"],
      tags: ["atmospheric", "cinematic", "background"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-03-22",
      description: "Atmospheric instrumental piece with haunting melodies and ambient textures."
    },
    {
      id: 3,
      title: "Luanda Spotlights",
      artist: "Jeyslee",
      duration: "3:40",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee-Luanda+SpotlightsV2.mp3",
      category: "soundtracks",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "d_major",
      bpm: 124,
      instruments: ["guitar", "drums", "bass", "synthesizer"],
      tags: ["upbeat", "african", "rhythmic"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-06-10",
      description: "Vibrant track with African influences and modern electronic elements."
    },
    {
      id: 4,
      title: "Control",
      artist: "Jeyslee",
      duration: "2:26",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "soundtracks",
      mood: ["epic", "energetic"],
      tempo: "medium",
      key: "e_minor",
      bpm: 110,
      instruments: ["synthesizer", "drums", "piano"],
      tags: ["dramatic", "powerful", "cinematic"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-04-05",
      description: "Powerful and dramatic composition with driving rhythms and epic crescendos."
    },
    {
      id: 5,
      title: "EDLV Hymne",
      artist: "Jeyslee",
      duration: "3:17",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "corporate"],
      tempo: "medium",
      key: "c_major",
      bpm: 100,
      instruments: ["piano", "strings", "brass", "drums"],
      tags: ["anthem", "inspirational", "majestic"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-02-18",
      description: "Majestic anthem with inspirational themes and orchestral arrangements."
    },
    {
      id: 61,
      title: "La Voie (Voix)",
      artist: "Jeyslee",
      duration: "3:52",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/Jeyslee+-+La+Voie.mp3",
      category: "voiceovers",
      mood: ["inspirational", "romantic"],
      tempo: "medium",
      key: "g_minor",
      bpm: 95,
      instruments: ["vocals", "piano", "strings"],
      tags: ["voice", "French", "emotional"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-03-22",
      description: "Version avec voix du titre 'La Voie'."
    },
    {
      id: 6,
      title: "BYD",
      artist: "Jeyslee",
      duration: "0:40",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["corporate", "energetic"],
      tempo: "fast",
      key: "a_major",
      bpm: 130,
      instruments: ["synthesizer", "piano", "drums"],
      tags: ["short", "logo", "branding"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-07-01",
      description: "Short and impactful jingle perfect for brand identity and commercials."
    },
    {
      id: 7,
      title: "Retroactive",
      artist: "Jeyslee",
      duration: "1:05",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Retroactive+%5BInstrumental+v1%5D.wav",
      category: "sound_design",
      mood: ["mysterious", "calm"],
      tempo: "slow",
      key: "f_minor",
      bpm: 75,
      instruments: ["synthesizer", "piano", "electronic"],
      tags: ["ambient", "atmospheric", "background"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-01-30",
      description: "Atmospheric sound design piece with retro-futuristic elements and textures."
    },
    {
      id: 8,
      title: "ZONE",
      artist: "Jeyslee & Yūutsu",
      duration: "3:14",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+%26+Y%C5%AButsu+-+Zone.mp3",
      category: "soundtracks",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "g_major",
      bpm: 128,
      instruments: ["synthesizer", "drums", "bass", "vocals"],
      tags: ["electronic", "dance", "collaboration"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-08-12",
      description: "Collaborative electronic track with catchy melodies and driving beats."
    },
    {
      id: 9,
      title: "SIMA",
      artist: "Amilcar JOAO",
      duration: "1:58",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Voice_Update/haleine+dingue.wav",
      category: "voiceovers",
      mood: ["corporate"],
      tempo: "medium",
      key: "n/a",
      bpm: 0,
      instruments: ["vocals"],
      tags: ["professional", "clear", "corporate"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-05-20",
      description: "Professional voiceover recording with clear articulation and corporate tone."
    },
    {
      id: 10,
      title: "CIRMI WEBINAIRE",
      artist: "Amilcar Joao",
      duration: "0:30",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Voice_Update/Cirmi+webinaire.mp3",
      category: "voiceovers",
      mood: ["corporate", "calm"],
      tempo: "medium",
      key: "n/a",
      bpm: 0,
      instruments: ["vocals"],
      tags: ["webinar", "professional", "informative"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-06-05",
      description: "Short professional voiceover perfect for webinars and online presentations."
    }
    ,
    {
      id: 11,
      title: "Neon Skyline",
      artist: "Applisound Collective",
      duration: "2:48",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "soundtracks",
      mood: ["energetic", "uplifting"],
      tempo: "fast",
      key: "e_major",
      bpm: 132,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["synthwave", "city", "drive"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-01",
      description: "Bright synth melodies over a driving beat for modern commercials."
    },
    {
      id: 12,
      title: "Soft Dawn Piano",
      artist: "Jeyslee",
      duration: "3:06",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["calm", "romantic"],
      tempo: "slow",
      key: "a_minor",
      bpm: 72,
      instruments: ["piano", "strings"],
      tags: ["ambient", "piano", "warm"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-05",
      description: "Gentle piano theme with lush strings for emotive storytelling."
    },
    {
      id: 13,
      title: "Brand Stinger Alpha",
      artist: "Applisound Studio",
      duration: "0:12",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["corporate", "happy"],
      tempo: "fast",
      key: "c_major",
      bpm: 128,
      instruments: ["synthesizer", "trumpet", "drums"],
      tags: ["logo", "intro", "short"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-07",
      description: "Short, memorable stinger for brand intros and outros."
    },
    {
      id: 14,
      title: "Midnight Runner",
      artist: "Jeyslee",
      duration: "2:59",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+%26+Y%C5%AButsu+-+Zone.mp3",
      category: "soundtracks",
      mood: ["energetic", "mysterious"],
      tempo: "fast",
      key: "b_minor",
      bpm: 126,
      instruments: ["synthesizer", "drums", "guitar"],
      tags: ["night", "motion", "edgy"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-10",
      description: "Driving electro with a hint of suspense for dynamic visuals."
    },
    {
      id: 15,
      title: "Corporate Horizons",
      artist: "Applisound Collective",
      duration: "2:21",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "d_major",
      bpm: 112,
      instruments: ["piano", "guitar", "drums"],
      tags: ["presentation", "startup", "tech"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-14",
      description: "Motivational corporate track ideal for product launches and slides."
    },
    {
      id: 16,
      title: "Whispers in Glass",
      artist: "Jeyslee",
      duration: "3:15",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["mysterious", "dramatic"],
      tempo: "medium",
      key: "g_sharp_minor",
      bpm: 98,
      instruments: ["strings", "synthesizer", "piano"],
      tags: ["suspense", "noir", "score"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-18",
      description: "Eerie textures and evolving strings for cinematic tension."
    },
    {
      id: 17,
      title: "Ocean Lights",
      artist: "Applisound Collective",
      duration: "3:31",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee-Luanda+SpotlightsV2.mp3",
      category: "sound_design",
      mood: ["calm", "mysterious"],
      tempo: "slow",
      key: "e_minor",
      bpm: 78,
      instruments: ["synthesizer", "flute", "ambient"],
      tags: ["ocean", "ambient", "soft"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-20",
      description: "Ambient swells and gentle pads for underwater visuals."
    },
    {
      id: 18,
      title: "Hero’s Path",
      artist: "Jeyslee",
      duration: "2:58",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "a_major",
      bpm: 102,
      instruments: ["brass", "strings", "drums"],
      tags: ["anthem", "majestic", "orchestral"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-22",
      description: "Triumphant orchestral theme with bold brass and rolling drums."
    },
    {
      id: 19,
      title: "Spark Logo Beta",
      artist: "Applisound Studio",
      duration: "0:09",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["happy", "energetic"],
      tempo: "fast",
      key: "e_major",
      bpm: 130,
      instruments: ["synthesizer", "trumpet"],
      tags: ["intro", "brand", "spark"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-09-25",
      description: "Quick logo sting with bright brass hits and a sparkle."
    },
    {
      id: 20,
      title: "Velvet Corridor",
      artist: "Jeyslee",
      duration: "3:04",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "soundtracks",
      mood: ["romantic", "calm"],
      tempo: "slow",
      key: "f_sharp_minor",
      bpm: 80,
      instruments: ["piano", "strings", "saxophone"],
      tags: ["late night", "jazz", "soft"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-01",
      description: "Late-night mood with warm piano and silky saxophone."
    },
    {
      id: 21,
      title: "Brave New Start",
      artist: "Applisound Collective",
      duration: "2:36",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "c_major",
      bpm: 110,
      instruments: ["guitar", "piano", "drums"],
      tags: ["team", "launch", "success"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-03",
      description: "Optimistic corporate theme for team success and milestones."
    },
    {
      id: 22,
      title: "Northern Lights",
      artist: "Jeyslee",
      duration: "3:22",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["inspirational", "epic"],
      tempo: "medium",
      key: "g_major",
      bpm: 104,
      instruments: ["strings", "piano", "ambient"],
      tags: ["nature", "aurora", "wide"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-05",
      description: "Expansive orchestral textures inspired by northern skies."
    },
    {
      id: 23,
      title: "Studio Whisper VO",
      artist: "Amilcar JOAO",
      duration: "0:45",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Voice_Update/Cirmi+webinaire.mp3",
      category: "voiceovers",
      mood: ["corporate"],
      tempo: "medium",
      key: "n/a",
      bpm: 0,
      instruments: ["vocals"],
      tags: ["voiceover", "studio", "clean"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-07",
      description: "Clean and confident voiceover for corporate reels."
    },
    {
      id: 24,
      title: "Retro Arcade Night",
      artist: "Jeyslee",
      duration: "1:59",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Retroactive+%5BInstrumental+v1%5D.wav",
      category: "sound_design",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "e_minor",
      bpm: 128,
      instruments: ["synthesizer", "electronic"],
      tags: ["retro", "arcade", "neon"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-10",
      description: "Chiptune-inspired pulses for playful retro vibes."
    },
    {
      id: 25,
      title: "Morning Breeze",
      artist: "Applisound Collective",
      duration: "2:44",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "soundtracks",
      mood: ["calm", "happy"],
      tempo: "medium",
      key: "g_major",
      bpm: 100,
      instruments: ["guitar", "piano", "flute"],
      tags: ["morning", "fresh", "light"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-12",
      description: "Refreshing acoustic textures for lifestyle and travel content."
    },
    {
      id: 26,
      title: "Urban Drive",
      artist: "Jeyslee",
      duration: "2:12",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "corporate",
      mood: ["energetic", "inspirational"],
      tempo: "fast",
      key: "b_major",
      bpm: 128,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["city", "tech", "ad"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-15",
      description: "Punchy electronic corporate for product ads and promos."
    },
    {
      id: 27,
      title: "Amber Memories",
      artist: "Applisound Collective",
      duration: "3:08",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["romantic", "sad"],
      tempo: "slow",
      key: "a_minor",
      bpm: 70,
      instruments: ["piano", "violin", "cello"],
      tags: ["nostalgia", "memories", "soft"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-18",
      description: "Emotional piano with strings evoking memories and warmth."
    },
    {
      id: 28,
      title: "Pulse Logo Gamma",
      artist: "Applisound Studio",
      duration: "0:07",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["energetic"],
      tempo: "fast",
      key: "d_major",
      bpm: 132,
      instruments: ["synthesizer"],
      tags: ["logo", "pulse", "intro"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-20",
      description: "Ultra-short pulse for quick brand transitions."
    },
    {
      id: 29,
      title: "Golden Hour Fields",
      artist: "Jeyslee",
      duration: "2:53",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "soundtracks",
      mood: ["happy", "uplifting"],
      tempo: "medium",
      key: "c_major",
      bpm: 108,
      instruments: ["guitar", "piano", "drums"],
      tags: ["travel", "family", "sunset"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-22",
      description: "Warm acoustic pop perfect for lifestyle reels and vlogs."
    },
    {
      id: 30,
      title: "Vox Clarity",
      artist: "Amilcar JOAO",
      duration: "1:02",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Voice_Update/haleine+dingue.wav",
      category: "voiceovers",
      mood: ["corporate"],
      tempo: "medium",
      key: "n/a",
      bpm: 0,
      instruments: ["vocals"],
      tags: ["clear", "professional", "narration"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-25",
      description: "Focused narration with precise diction for explainers."
    },
    {
      id: 31,
      title: "Quantum Sparks",
      artist: "Applisound Collective",
      duration: "2:05",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Retroactive+%5BInstrumental+v1%5D.wav",
      category: "sound_design",
      mood: ["energetic", "dramatic"],
      tempo: "fast",
      key: "b_minor",
      bpm: 140,
      instruments: ["electronic", "synthesizer"],
      tags: ["tech", "glitch", "promo"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-10-28",
      description: "Glitchy impacts and risers for tech promos and reveals."
    },
    {
      id: 32,
      title: "Sunrise Over City",
      artist: "Jeyslee",
      duration: "3:10",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "e_major",
      bpm: 112,
      instruments: ["piano", "guitar", "drums"],
      tags: ["morning", "city", "optimism"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-01",
      description: "Positive corporate with bright guitars and piano."
    },
    {
      id: 33,
      title: "Crimson March",
      artist: "Applisound Collective",
      duration: "2:47",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "dramatic"],
      tempo: "medium",
      key: "d_major",
      bpm: 106,
      instruments: ["brass", "strings", "drums"],
      tags: ["anthem", "march", "hero"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-03",
      description: "Bold orchestral march with soaring brass themes."
    },
    {
      id: 34,
      title: "Sparkle ID Delta",
      artist: "Applisound Studio",
      duration: "0:11",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["happy"],
      tempo: "fast",
      key: "g_major",
      bpm: 128,
      instruments: ["synthesizer", "trumpet"],
      tags: ["sting", "brand", "sparkle"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-05",
      description: "Cheerful stinger for brand cues and transitions."
    },
    {
      id: 35,
      title: "Cascade Stories",
      artist: "Jeyslee",
      duration: "3:18",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "cinematic",
      mood: ["inspirational", "romantic"],
      tempo: "medium",
      key: "c_sharp_minor",
      bpm: 96,
      instruments: ["piano", "strings"],
      tags: ["documentary", "nature", "flow"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-08",
      description: "Flowing orchestral textures for scenic storytelling."
    },
    {
      id: 36,
      title: "Electric Roads",
      artist: "Applisound Collective",
      duration: "2:27",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "soundtracks",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "e_major",
      bpm: 130,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["driving", "modern", "youth"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-10",
      description: "Electric pop energy for fast-paced edits and ads."
    },
    {
      id: 37,
      title: "Deep Blue Echoes",
      artist: "Jeyslee",
      duration: "3:41",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Retroactive+%5BInstrumental+v1%5D.wav",
      category: "sound_design",
      mood: ["mysterious", "calm"],
      tempo: "slow",
      key: "e_minor",
      bpm: 76,
      instruments: ["ambient", "synthesizer"],
      tags: ["underwater", "echo", "space"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-12",
      description: "Submerged pads and echoes for calm, mysterious scenes."
    },
    {
      id: 38,
      title: "Rising Lights",
      artist: "Applisound Collective",
      duration: "2:42",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "g_major",
      bpm: 112,
      instruments: ["guitar", "piano", "drums"],
      tags: ["promo", "launch", "ambition"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-14",
      description: "Steady inspirational build for brand and product videos."
    },
    {
      id: 39,
      title: "Emerald Anthem",
      artist: "Jeyslee",
      duration: "3:01",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "e_major",
      bpm: 104,
      instruments: ["strings", "brass", "drums"],
      tags: ["anthem", "victory", "grand"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-16",
      description: "Uplifting orchestral anthem with proud brass."
    },
    {
      id: 40,
      title: "Logo Pop Echo",
      artist: "Applisound Studio",
      duration: "0:08",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["happy", "energetic"],
      tempo: "fast",
      key: "a_major",
      bpm: 128,
      instruments: ["synthesizer"],
      tags: ["logo", "pop", "echo"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-18",
      description: "Pop-flavored logo sting with airy echo."
    },
    {
      id: 41,
      title: "City Rain Reflections",
      artist: "Jeyslee",
      duration: "3:09",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["sad", "mysterious"],
      tempo: "slow",
      key: "g_sharp_minor",
      bpm: 78,
      instruments: ["piano", "strings"],
      tags: ["rain", "city", "noir"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-20",
      description: "Moody piano score with gentle string swells."
    },
    {
      id: 42,
      title: "Nimbus Drive",
      artist: "Applisound Collective",
      duration: "2:26",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "soundtracks",
      mood: ["energetic", "happy"],
      tempo: "fast",
      key: "d_major",
      bpm: 128,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["car", "adrenaline", "modern"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-23",
      description: "High-energy synth pop for motion-heavy cuts."
    },
    {
      id: 43,
      title: "Vivid Horizons",
      artist: "Jeyslee",
      duration: "2:55",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "corporate",
      mood: ["uplifting", "inspirational"],
      tempo: "medium",
      key: "g_major",
      bpm: 110,
      instruments: ["guitar", "piano", "drums"],
      tags: ["vision", "growth", "team"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-25",
      description: "Confident corporate pop with shimmering guitars."
    },
    {
      id: 44,
      title: "Silver Crest",
      artist: "Applisound Collective",
      duration: "3:16",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee-Luanda+SpotlightsV2.mp3",
      category: "soundtracks",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "d_major",
      bpm: 118,
      instruments: ["strings", "synthesizer", "drums"],
      tags: ["mountain", "hero", "wide"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-11-28",
      description: "Hybrid orchestral build for sweeping drone footage."
    },
    {
      id: 45,
      title: "Aurora Hymn",
      artist: "Jeyslee",
      duration: "2:49",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "c_major",
      bpm: 100,
      instruments: ["strings", "brass", "drums"],
      tags: ["anthem", "light", "grand"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-01",
      description: "Radiant orchestral theme with triumphant peaks."
    },
    {
      id: 46,
      title: "Pixel Spark ID",
      artist: "Applisound Studio",
      duration: "0:10",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["happy"],
      tempo: "fast",
      key: "g_major",
      bpm: 130,
      instruments: ["synthesizer"],
      tags: ["pixel", "logo", "retro"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-03",
      description: "Retro pixel-styled logo sting with a modern twist."
    },
    {
      id: 47,
      title: "Serene Letters",
      artist: "Applisound Collective",
      duration: "3:26",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["calm", "romantic"],
      tempo: "slow",
      key: "a_minor",
      bpm: 72,
      instruments: ["piano", "violin"],
      tags: ["letters", "vintage", "warm"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-05",
      description: "A gentle piano and violin theme for intimate moments."
    },
    {
      id: 48,
      title: "Cosmic Lanes",
      artist: "Jeyslee",
      duration: "2:18",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Retroactive+%5BInstrumental+v1%5D.wav",
      category: "sound_design",
      mood: ["mysterious", "energetic"],
      tempo: "medium",
      key: "f_sharp_minor",
      bpm: 92,
      instruments: ["electronic", "synthesizer"],
      tags: ["space", "particles", "motion"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-08",
      description: "Cosmic pulses and whooshes for sci-fi visuals."
    },
    {
      id: 49,
      title: "Clarity Pitch",
      artist: "Applisound Collective",
      duration: "2:34",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "e_major",
      bpm: 108,
      instruments: ["piano", "guitar", "drums"],
      tags: ["pitch", "deck", "clear"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-10",
      description: "Crisp corporate motif ideal for pitch decks."
    },
    {
      id: 50,
      title: "Golden Anthem",
      artist: "Jeyslee",
      duration: "2:58",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "d_major",
      bpm: 104,
      instruments: ["strings", "brass", "drums"],
      tags: ["anthem", "gold", "rise"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-12",
      description: "Inspired orchestral theme with a golden glow."
    },
    {
      id: 51,
      title: "Logo Nova",
      artist: "Applisound Studio",
      duration: "0:06",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["energetic"],
      tempo: "fast",
      key: "e_major",
      bpm: 132,
      instruments: ["synthesizer"],
      tags: ["nova", "id", "brand"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-14",
      description: "Brisk logo accent with a bright synth pop."
    },
    {
      id: 52,
      title: "Paper Boats",
      artist: "Applisound Collective",
      duration: "3:02",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["calm", "romantic"],
      tempo: "slow",
      key: "a_minor",
      bpm: 74,
      instruments: ["piano", "flute"],
      tags: ["childhood", "memories", "soft"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-16",
      description: "Gentle, picturesque piano score for heartfelt scenes."
    },
    {
      id: 53,
      title: "Circuit Run",
      artist: "Jeyslee",
      duration: "2:14",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/bad-boy-radio-edit.mp3",
      category: "soundtracks",
      mood: ["energetic", "dramatic"],
      tempo: "fast",
      key: "b_minor",
      bpm: 134,
      instruments: ["synthesizer", "drums", "bass"],
      tags: ["race", "speed", "tech"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-18",
      description: "Fast-paced electronic with sharp rhythmic hooks."
    },
    {
      id: 54,
      title: "Silent Frames",
      artist: "Applisound Collective",
      duration: "3:12",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "cinematic",
      mood: ["mysterious", "sad"],
      tempo: "slow",
      key: "e_minor",
      bpm: 78,
      instruments: ["piano", "cello"],
      tags: ["film", "frame", "soft"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-20",
      description: "Sparse piano and cello for reflective cinematic scenes."
    },
    {
      id: 55,
      title: "Amber Skyline",
      artist: "Jeyslee",
      duration: "2:51",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+%26+Y%C5%AButsu+-+Zone.mp3",
      category: "soundtracks",
      mood: ["happy", "uplifting"],
      tempo: "medium",
      key: "d_major",
      bpm: 118,
      instruments: ["synthesizer", "guitar", "drums"],
      tags: ["city", "lights", "glow"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-22",
      description: "Feel-good electro-pop with warm synth textures."
    },
    {
      id: 56,
      title: "Skyline Pitch",
      artist: "Applisound Collective",
      duration: "2:24",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Control.mp3",
      category: "corporate",
      mood: ["inspirational", "uplifting"],
      tempo: "medium",
      key: "c_major",
      bpm: 110,
      instruments: ["guitar", "piano", "drums"],
      tags: ["slides", "pitch", "clean"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-24",
      description: "Clean corporate underscore for modern pitch decks."
    },
    {
      id: 57,
      title: "Aurora Rise",
      artist: "Jeyslee",
      duration: "3:05",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3",
      category: "hymns",
      mood: ["epic", "inspirational"],
      tempo: "medium",
      key: "e_major",
      bpm: 104,
      instruments: ["strings", "brass", "drums"],
      tags: ["rise", "proud", "anthem"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-26",
      description: "A rising orchestral anthem with proud brass lines."
    },
    {
      id: 58,
      title: "Pixel Pop ID",
      artist: "Applisound Studio",
      duration: "0:13",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Build+Your+Dreams+2.wav",
      category: "jingles",
      mood: ["happy", "energetic"],
      tempo: "fast",
      key: "g_major",
      bpm: 128,
      instruments: ["synthesizer"],
      tags: ["pixel", "cute", "logo"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-28",
      description: "Cute pixel-styled ID with a cheerful cadence."
    },
    {
      id: 59,
      title: "Letters to Sea",
      artist: "Applisound Collective",
      duration: "3:19",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/la-voie-instru.mp3",
      category: "cinematic",
      mood: ["romantic", "calm"],
      tempo: "slow",
      key: "a_minor",
      bpm: 70,
      instruments: ["piano", "violin", "flute"],
      tags: ["ocean", "letters", "gentle"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2023-12-30",
      description: "A tender orchestral letter carried by the sea breeze."
    },
    {
      id: 60,
      title: "Night Runner V2",
      artist: "Jeyslee",
      duration: "2:45",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+%26+Y%C5%AButsu+-+Zone.mp3",
      category: "soundtracks",
      mood: ["energetic", "mysterious"],
      tempo: "fast",
      key: "b_minor",
      bpm: 126,
      instruments: ["synthesizer", "drums", "guitar"],
      tags: ["night", "drive", "city"],
      featured: true,
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2024-01-02",
      description: "A refreshed take on the neon-night synth drive."
    },
    {
      id: 62,
      title: "Not A Goodbye V4",
      artist: "Jeyslee",
      duration: "3:34",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+Not+A+Goodbye+V4.mp3",
      category: "soundtracks",
      mood: ["romantic", "inspirational"],
      tempo: "medium",
      key: "a_minor",
      bpm: 96,
      instruments: ["piano", "synthesizer", "drums"],
      tags: ["emotive", "melodic", "radio"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2024-01-10",
      description: "A heartfelt modern pop ballad with warm keys and gentle drums."
    },
    {
      id: 63,
      title: "Cette Année 1 [Instrumental]",
      artist: "Jeyslee & Gizèle",
      duration: "3:48",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+%26+Giz%C3%A8le+-+Cette+Ann%C3%A9e+1+%5BInstrumental%5D.mp3",
      category: "soundtracks",
      mood: ["happy", "uplifting"],
      tempo: "medium",
      key: "g_major",
      bpm: 110,
      instruments: ["guitar", "drums", "bass", "synthesizer"],
      tags: ["instrumental", "feel-good", "anthemic"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2024-01-11",
      description: "An upbeat instrumental anthem with catchy hooks and bright energy."
    },
    {
      id: 64,
      title: "Zala Nzambe Na Nga (Jeyslee House Edit)",
      artist: "Rabbi Muya",
      duration: "5:26",
      url: "https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Rabbi+Muya+-+Zala+Nzambe+Na+Nga+(Jeyslee+House+Edit).flac",
      category: "soundtracks",
      mood: ["energetic", "inspirational"],
      tempo: "fast",
      key: "e_minor",
      bpm: 124,
      instruments: ["drums", "bass", "synthesizer", "vocals"],
      tags: ["house", "edit", "spiritual"],
      imageUrl: "https://applisound-images.s3.eu-west-3.amazonaws.com/zone.png",
      releaseDate: "2024-01-12",
      description: "A driving house rework blending uplifting grooves with spiritual motifs."
    }
  ];
  
  constructor(private http: HttpClient) {
    // Initialize data
    this.loadInitialData();
  }
  
  // Initialize data from mock or API
  private loadInitialData(): void {
    // In a real application, these would be API calls
    this.loadCategories();
    this.loadMoods();
    this.loadTempos();
    this.loadKeys();
    this.loadInstruments();
    this.loadTracks();
  }
  
  // Load categories
  private loadCategories(): void {
    // In a real app: this.http.get<TrackCategory[]>(`${this.apiUrl}/categories`)
    this.categoriesSubject.next(this.mockCategories);
  }
  
  // Load moods
  private loadMoods(): void {
    // In a real app: this.http.get<TrackMood[]>(`${this.apiUrl}/moods`)
    this.moodsSubject.next(this.mockMoods);
  }
  
  // Load tempos
  private loadTempos(): void {
    // In a real app: this.http.get<TrackTempo[]>(`${this.apiUrl}/tempos`)
    this.temposSubject.next(this.mockTempos);
  }
  
  // Load keys
  private loadKeys(): void {
    // In a real app: this.http.get<TrackKey[]>(`${this.apiUrl}/keys`)
    this.keysSubject.next(this.mockKeys);
  }
  
  // Load instruments
  private loadInstruments(): void {
    // In a real app: this.http.get<TrackInstrument[]>(`${this.apiUrl}/instruments`)
    this.instrumentsSubject.next(this.mockInstruments);
  }
  
  // Load tracks
  private loadTracks(): void {
    // In a real app: this.http.get<Track[]>(`${this.apiUrl}/tracks`)
    this.tracksSubject.next(this.mockTracks);
  }
  
  // Get all tracks
  getTracks(): Observable<Track[]> {
    return this.tracks$;
  }
  
  // Get featured tracks
  getFeaturedTracks(): Observable<Track[]> {
    return this.tracks$.pipe(
      map(tracks => tracks.filter(track => track.featured))
    );
  }
  
  // Get track by ID
  getTrackById(id: number): Observable<Track | undefined> {
    return this.tracks$.pipe(
      map(tracks => tracks.find(track => track.id === id))
    );
  }
  
  // Filter tracks by multiple criteria
  filterTracks(
    searchQuery: string = '',
    category: string = 'all',
    mood: string = 'all',
    tempo: string = 'all',
    key: string = 'all',
    instrument: string = 'all'
  ): Observable<Track[]> {
    return this.tracks$.pipe(
      map(tracks => {
        return tracks.filter(track => {
          // Search query filter
          const searchMatch = searchQuery === '' || 
            track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            track.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (track.description && track.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (track.tags && track.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
          
          // Category filter
          const categoryMatch = category === 'all' || track.category === category;
          
          // Mood filter
          const moodMatch = mood === 'all' || (track.mood && track.mood.includes(mood));
          
          // Tempo filter
          const tempoMatch = tempo === 'all' || track.tempo === tempo;
          
          // Key filter
          const keyMatch = key === 'all' || track.key === key;
          
          // Instrument filter
          const instrumentMatch = instrument === 'all' || 
            (track.instruments && track.instruments.includes(instrument));
          
          return searchMatch && categoryMatch && moodMatch && tempoMatch && keyMatch && instrumentMatch;
        });
      })
    );
  }
  
  // Request track download
  requestDownload(request: DownloadRequest): Observable<any> {
    // In a real app: return this.http.post(`${this.apiUrl}/tracks/download-request`, request);
    console.log('Download requested:', request);
    return of({ success: true, message: 'Download request received. Check your email for the download link.' });
  }
  
  // Get similar tracks
  getSimilarTracks(trackId: number): Observable<Track[]> {
    return this.tracks$.pipe(
      map(tracks => {
        const originalTrack = tracks.find(t => t.id === trackId);
        if (!originalTrack) return [];
        
        return tracks
          .filter(t => t.id !== trackId) // Exclude the original track
          .filter(t => {
            // Match by category and mood
            const categoryMatch = t.category === originalTrack.category;
            const moodMatch = t.mood.some(m => originalTrack.mood.includes(m));
            return categoryMatch || moodMatch;
          })
          .slice(0, 4); // Limit to 4 similar tracks
      })
    );
  }
  
  // Get tracks by category
  getTracksByCategory(categoryId: string): Observable<Track[]> {
    return this.filterTracks('', categoryId);
  }
  
  // Get tracks by mood
  getTracksByMood(moodId: string): Observable<Track[]> {
    return this.filterTracks('', 'all', moodId);
  }
}
